<!-- sections/BenchmarksSection.vue -->
<script setup lang="ts">
import { ref, watch } from 'vue';
import BaseSection from '../components/BaseSection.vue';
import ConnxField, { ErrorUpdate } from '@/components/Molecules/Field/ConnxField.vue';
import { InputType } from '@/interfaces/InputFields';
import { isNullEmptyOrZero } from '../utils/validation-helpers';

interface Props {
  benchmarks?: {
    label: string;
    levels: Array<{
      label: string;
      color: string;
      operator: string;
    }>;
    values: (number | null)[];
    unit?: string;
  };
  errors?: Map<string, string | null>;
  showTitle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => new Map(),
  showTitle: true,
});

const emit = defineEmits<{
  'field-error': [{ name: string; error: string | null }];
  'update:benchmarks': [{ values: (number | null)[] }];
}>();

const benchmarkFirst = ref(props.benchmarks?.values[0] ?? null);
const benchmarkSecond = ref(props.benchmarks?.values[1] ?? null);

const handleFieldError = (error: ErrorUpdate) => {
  emit('field-error', error);
};

// Watch for benchmark changes
watch(
  () => [benchmarkFirst.value, benchmarkSecond.value],
  ([newFirst, newSecond], [oldFirst, oldSecond]) => {
    if (
      (oldFirst !== newFirst && !isNullEmptyOrZero(newFirst)) ||
      (oldSecond !== newSecond && !isNullEmptyOrZero(newSecond))
    ) {
      emit('update:benchmarks', {
        values: [newFirst, newSecond],
      });
    }
  }
);
</script>

<template>
  <BaseSection :title="benchmarks?.label" :should-show="!!benchmarks" :show-title="showTitle">
    <div class="tw-w-auto">
      <div class="tw-grid tw-grid-cols-[auto_auto_auto] tw-gap-y-4 tw-gap-x-6 tw-items-center tw-w-fit">
        <template v-for="(level, index) in benchmarks?.levels" :key="level.label">
          <div class="tw-flex tw-items-center tw-gap-x-4">
            <div class="tw-w-8 tw-h-8 tw-rounded-sm" :style="{ background: level.color }"></div>
            <label :for="index === 0 ? 'benchmarkFirst' : 'benchmarkSecond'" class="tw-m-0">
              {{ level.label }}
            </label>
          </div>
          <div class="tw-text-lg tw-font-bold">
            {{ level.operator }}
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <ConnxField
              v-if="index === 0"
              name="benchmarkFirst"
              v-model="benchmarkFirst"
              class="tw-m-0 tw-w-52"
              required
              :type="InputType.Number"
              @update:error="handleFieldError"
            />
            <ConnxField
              v-else
              :name="index === 1 ? 'benchmarkSecond' : 'benchmarkThird'"
              v-model="benchmarkSecond"
              :required="index === 1"
              class="tw-m-0 tw-w-52"
              :disabled="index === 2"
              :type="InputType.Number"
              @update:error="handleFieldError"
            />
            <div v-if="benchmarks?.unit">
              {{ benchmarks.unit }}
            </div>
          </div>
        </template>
      </div>
    </div>
  </BaseSection>
</template>
